// src/store/rankingStore.ts
// RankingStore — manages real-time ranking state from rank_changed events.
//
// Architecture:
//   rank_changed WS event
//       ↓
//   wsClient.on('rank_changed', handler)
//       ↓
//   RankingStore.apply(event) — O(1) update
//       ↓
//   ScoreRankingPanel — row-level update (not full re-render)
//
// Unlike uiFrameStore (full snapshots), RankingStore is event-driven.
// Only updated when rank/score actually changes.

import { create } from 'zustand';
import type { ConnectionStatus } from '../api/wsClient';

export interface RankEntry {
  symbol: string;
  score: number;
  rank: number;
  prevRank?: number;
  scoreDelta?: number;
}

export interface RankChangedEvent {
  type: 'rank.changed';
  symbol: string;
  old_rank: number | null;
  new_rank: number;
  old_score: number;
  new_score: number;
  rank_changed: boolean;
  cause: string;
  model_version: string;
  bar_hash: string;
  seq: number;
}

export interface RankingState {
  // Top-N ranking (sorted by score, maintained incrementally)
  top50: RankEntry[];

  // Index: symbol → position in top50 array (O(1) lookup)
  rankIndex: Map<string, number>;

  // Stats
  lastEventTs: number;
  totalRankChanges: number;
  connectionStatus: ConnectionStatus;
}

interface RankingActions {
  // Apply a rank_changed event (O(1))
  apply(event: RankChangedEvent): void;

  // Bulk reset (for mode switch / reconnect)
  reset(): void;

  // Connection status from wsClient
  setConnectionStatus(status: ConnectionStatus): void;
}

type RankingStore = RankingState & RankingActions;

export const useRankingStore = create<RankingStore>((set, get) => ({
  top50: [],
  rankIndex: new Map(),
  lastEventTs: 0,
  totalRankChanges: 0,
  connectionStatus: 'disconnected',

  apply(event: RankChangedEvent) {
    const { symbol, new_rank, new_score, old_rank, old_score, rank_changed } = event;

    set(state => {
      const newTop50 = [...state.top50];
      const rankIndex = new Map(state.rankIndex);

      // Compute delta
      const scoreDelta = old_score ? new_score - old_score : undefined;

      if (rank_changed && old_rank !== null) {
        // Rank changed: remove from old position, insert at new position
        const oldIdx = rankIndex.get(symbol);
        if (oldIdx !== undefined && oldIdx >= 0 && oldIdx < newTop50.length) {
          newTop50.splice(oldIdx, 1);
        }
        // Insert at new position (new_rank is 1-based)
        const insertIdx = new_rank - 1;
        newTop50.splice(insertIdx, 0, {
          symbol,
          score: new_score,
          rank: new_rank,
          prevRank: old_rank ?? undefined,
          scoreDelta,
        });
      } else {
        // Score changed but rank didn't (or new symbol): update in place
        const existingIdx = rankIndex.get(symbol);
        if (existingIdx !== undefined && existingIdx >= 0 && existingIdx < newTop50.length) {
          newTop50[existingIdx] = {
            symbol,
            score: new_score,
            rank: new_rank,
            prevRank: old_rank ?? undefined,
            scoreDelta,
          };
        } else {
          // New symbol entering ranking
          const insertIdx = new_rank - 1;
          newTop50.splice(insertIdx, 0, {
            symbol,
            score: new_score,
            rank: new_rank,
            prevRank: old_rank ?? undefined,
            scoreDelta,
          });
        }
      }

      // Rebuild rank index
      rankIndex.clear();
      newTop50.forEach((entry, idx) => {
        rankIndex.set(entry.symbol, idx);
      });

      return {
        top50: newTop50,
        rankIndex,
        lastEventTs: Date.now(),
        totalRankChanges: state.totalRankChanges + 1,
      };
    });
  },

  reset() {
    set({
      top50: [],
      rankIndex: new Map(),
      lastEventTs: 0,
      totalRankChanges: 0,
    });
  },

  setConnectionStatus(status) {
    set({ connectionStatus: status });
  },
}));

// ---- Selectors ----

export const selectTop50 = (state: RankingStore) => state.top50;
export const selectRankOf = (symbol: string) => (state: RankingStore) =>
  state.rankIndex.get(symbol);
export const selectTotalRankChanges = (state: RankingStore) => state.totalRankChanges;
